import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { errorHandler } from 'app/utils/errorHandler';
import { URLS } from 'app/config/urls';
import { linesListColumns, linesStripListColumns } from '../table-configs/columns-schemas';
import { Button, Grid, makeStyles } from '@material-ui/core';
import * as Popover from '../components/PopoverMenu';
import DefaultTable from '../components/DefaultTable';
import PageWrapper from '../components/PageWrapper';
import { clearSelectedLine, setParentLine } from 'app/store/slices/line';
import ListsFilters from '../components/ListsFilters';
import { generatePath, useRouteMatch } from 'react-router-dom';
import lineEndpoint from '../../../api/services/lineEndpoint';
import { useTableReducer } from '../hooks/useTableReducer';
import { usePersistentFilters } from '../hooks/usePersistentFilters';

const LinesList = () => {
    const history = useHistory();
    const classes = useStyles();
    const { params } = useRouteMatch();

    const [lines, actions] = useTableReducer();
    const { changePage, changePageSize, resetTable, loadData: loadLines } = actions;

    const dispatch = useDispatch();
    const [editEl, setEditEl] = useState(null);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [loading, setLoading] = useState(false);

    const initialFilters = useMemo(() => ({
        campoOrdenacao: 'linha.nuLinha',
        direcaoOrdenacao: 'desc',
        boSlic: !params.isStrip,
    }), [params.isStrip]);

    const [filters, setFilters] = usePersistentFilters(initialFilters, 'lines-filters');

    const orderBy = filters.campoOrdenacao;
    const orderDirection = filters.direcaoOrdenacao;

    const open = Boolean(editEl);

    const isStrip = useSelector((state) => state.line.isStrip);

    useEffect(() => {
        dispatch(clearSelectedLine());
        (async () => {
            try {
                setLoading(true);
                const res = await lineEndpoint.getLines({
                    page: filters.page ? filters.page : lines.page,
                    size: filters.pageSize ? filters.pageSize : lines.pageSize,
                    request: filters
                });
                if (res.status === 200) {
                    if (res.data.content.length === 0) {
                        resetTable();
                    } else {
                        loadLines(res.data);
                    }
                }
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            } finally {
                setLoading(false);
            }
        })();
        //eslint-disable-next-line
    }, [lines.page, lines.pageSize, filters, dispatch]);

    useEffect(() => {
        setFilters((prevFilters) => ({ ...prevFilters, boSlic: !params.isStrip }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.isStrip]);

    const prepareDataToTable = (lineList) => {
        return lineList?.map((line) => {
            let result = {
                ...line,
                deNome: `${line.municipioOrigem?.nmMunicipio || ''} / ${line.municipioDestino?.nmMunicipio || ''}`,
                deRazaoSocial: `${line.empresa ? line.empresa.deRazaoSocial : ''}`,
            };
            if (isStrip && !line.cdLinhaPai) {
                result.lastButton = {
                    label: '+ NOVO SERVIÇO',
                    color: 'primary',
                    style: { marginLeft: '20px' },
                    onClick: (entry) => handleClickNewService(entry),
                };
            }
            return result;
        });
    };

    const handleOpenEditMenu = (event, data) => {
        setEditEl(event.currentTarget);
        setSelectedEntry(data);
    };

    const handleCloseEditMenu = () => {
        setSelectedEntry(null);
        setEditEl(null);
    };

    const handleNewLineButton = () => {
        let params = {};
        if (isStrip) params.isStrip = 'strip';
        history.push(generatePath(URLS.LINES_MANAGE_NEW, params));
    };

    const handleChangePageSize = (e) => {
        let pageSize = parseInt(e.target.value);
        changePageSize(pageSize);
        const temp = { ...filters };
        temp['pageSize'] = pageSize;
        setFilters(temp);
    };

    const handleChangePage = (e, page) => {
        changePage(page);
        const temp = { ...filters };
        temp['page'] = page;
        setFilters(temp);
        console.log({ page });
    };

    const handleChangeOrder = (event, property) => {
        setFilters(prev => {
            const tempDirection =
                property === orderBy ? (orderDirection === 'desc' ? 'asc' : 'desc') : orderDirection;

            return {
                ...prev,
                campoOrdenacao: property === 'deRazaoSocial'
                    ? `empresa.${property}`
                    : `linha.${property}`,
                direcaoOrdenacao: tempDirection,
            };
        });
        handleChangePage(null, 0);
    };

    const handleClickNewService = ({ data }) => {
        dispatch(setParentLine(data));
        handleNewLineButton();
    };

    const handleSetSearch = (filters) => {
        resetTable();
        setFilters(filters);
    };

    return (
        <>
            <PageWrapper
                title={`Lista de ${isStrip ? 'Linhas/Serviços' : 'Linhas'}`}
                actionButton={
                    <Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        className={classes.addButton}
                        onClick={handleNewLineButton}>
                        Nova linha
                    </Button>
                }>
                <Grid container spacing={3}>
                    <ListsFilters filters={filters} setFilters={handleSetSearch} hasSituation={false} />

                    <Grid item xs={12}>
                        <DefaultTable
                            columns={isStrip ? linesStripListColumns : linesListColumns}
                            rows={prepareDataToTable(lines?.content)}
                            loading={loading}
                            onMore={handleOpenEditMenu}
                            entryProps={{
                                invertIsActive: true,
                            }}
                            paginationProps={{
                                rowsPerPageOptions: [5, 10, 25],
                                count: lines.count,
                                rowsPerPage: filters.pageSize ? filters.pageSize : lines.pageSize,
                                page: filters.page ? filters.page : lines.page,
                                onPageChange: handleChangePage,
                                onRowsPerPageChange: handleChangePageSize,
                            }}
                            buttonsCellStyle={{
                                maxWidth: '200px',
                                minWidth: `${isStrip ? '220px' : '40px'}`,
                            }}
                            orderBy={orderBy}
                            order={orderDirection}
                            onRequestSort={handleChangeOrder}
                        />
                    </Grid>
                </Grid>
            </PageWrapper>
            <Popover.LineList
                open={open}
                anchorEl={editEl}
                handleClose={handleCloseEditMenu}
                dataId={selectedEntry?.cdLinha}
                isStrip={isStrip}
            />
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    addButton: {
        borderRadius: '25px',
        color: theme.palette.primary.contrastText,
    },
}));

export default LinesList;
